import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/cn'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import {
  FormModal,
  ModalField,
  modalInputClass,
} from '@/components/ui/FormModal'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { ApiError } from '@/services/api/client'
import { positionsApi } from '@/services/api/positions'
import type { Position } from '@/services/api/types'

type BlotterTab = 'open' | 'pending' | 'closed'

interface PositionRow {
  id: string
  symbol: string
  side: 'long' | 'short'
  quantity: number
  avgPrice: number
  unrealizedPnl: number
  last: number
  currency: string
  takeProfit?: number | null
  stopLoss?: number | null
}

interface PendingRow {
  id: string
  symbol: string
  side: 'buy' | 'sell'
  type: 'limit' | 'stop'
  quantity: number
  price: number
  currency: string
}

interface ClosedRow {
  id: string
  symbol: string
  side: 'long' | 'short'
  quantity: number
  entryPrice: number
  exitPrice: number
  realizedPnl: number
  currency: string
  closedAt: string
}

function mapOpen(p: Position): PositionRow {
  const open = Number(p.open_price)
  return {
    id: String(p.id),
    symbol: p.symbol ?? `ID:${p.instrument_id}`,
    side: p.side.value === 'buy' ? 'long' : 'short',
    quantity: Number(p.amount),
    avgPrice: open,
    unrealizedPnl: 0,
    last: open,
    currency: 'USD',
    takeProfit: p.take_profit ? Number(p.take_profit) : null,
    stopLoss: p.stop_loss ? Number(p.stop_loss) : null,
  }
}

function mapPending(p: Position): PendingRow {
  return {
    id: String(p.id),
    symbol: p.symbol ?? `ID:${p.instrument_id}`,
    side: p.side.value,
    type: 'limit',
    quantity: Number(p.amount),
    price: Number(p.open_price),
    currency: 'USD',
  }
}

function mapClosed(p: Position): ClosedRow {
  return {
    id: String(p.id),
    symbol: p.symbol ?? `ID:${p.instrument_id}`,
    side: p.side.value === 'buy' ? 'long' : 'short',
    quantity: Number(p.amount),
    entryPrice: Number(p.open_price),
    exitPrice: Number(p.close_price ?? p.open_price),
    realizedPnl: 0,
    currency: 'USD',
    closedAt: p.closed_at ?? p.updated_at,
  }
}

type ActionState =
  | { kind: 'close'; row: PositionRow }
  | { kind: 'partial'; row: PositionRow }
  | { kind: 'edit'; row: PendingRow }
  | { kind: 'delete'; row: PendingRow }
  | null

function ActionBtn({
  label,
  onClick,
  tone = 'default',
  icon,
}: {
  label: string
  onClick: () => void
  tone?: 'default' | 'danger'
  icon: 'close' | 'partial' | 'edit' | 'delete'
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
        tone === 'danger'
          ? 'text-danger hover:bg-danger/10'
          : 'text-accent hover:bg-accent/10',
      )}
    >
      <ActionIcon name={icon} />
    </button>
  )
}

function TabIcon({ name }: { name: BlotterTab }) {
  const cls = 'h-3.5 w-3.5 shrink-0'
  if (name === 'open') {
    return (
      <svg viewBox="0 0 16 16" className={cls} fill="none" aria-hidden>
        <rect
          x="2.5"
          y="3.5"
          width="11"
          height="9"
          rx="1.5"
          stroke="currentColor"
          strokeWidth="1.3"
        />
        <path
          d="M5 7.5h6M5 10h4"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    )
  }
  if (name === 'pending') {
    return (
      <svg viewBox="0 0 16 16" className={cls} fill="none" aria-hidden>
        <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3" />
        <path
          d="M8 5v3.2l2 1.3"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 16 16" className={cls} fill="none" aria-hidden>
      <path
        d="M3 11.5l3-3 2.5 2.5 4.5-5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 6h2v2"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ActionIcon({ name }: { name: 'close' | 'partial' | 'edit' | 'delete' }) {
  const cls = 'h-3.5 w-3.5'
  if (name === 'close') {
    return (
      <svg viewBox="0 0 16 16" className={cls} fill="none" aria-hidden>
        <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M5.5 8h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    )
  }
  if (name === 'partial') {
    return (
      <svg viewBox="0 0 16 16" className={cls} fill="none" aria-hidden>
        <path
          d="M3 12.5V3.5h4.5v9H3zM8.5 12.5V7h4.5v5.5H8.5z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  if (name === 'edit') {
    return (
      <svg viewBox="0 0 16 16" className={cls} fill="none" aria-hidden>
        <path
          d="M10.5 2.5l3 3L5.5 13.5H2.5v-3L10.5 2.5z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 16 16" className={cls} fill="none" aria-hidden>
      <path
        d="M3.5 4.5h9M6 4.5V3.5h4v1M5.5 4.5l.5 8h4l.5-8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Bottom blotter — Open / Pending / Closed tabs with action modals.
 */
export function PositionsPanel({
  className,
  initialTab = 'open',
}: {
  className?: string
  initialTab?: BlotterTab
}) {
  const { t } = useTranslation(['trading', 'common'])
  const { pushToast } = useToast()
  const { canTrade } = useAuth()
  const [tab, setTab] = useState<BlotterTab>(initialTab)
  const [openRows, setOpenRows] = useState<PositionRow[]>([])
  const [pendingRows, setPendingRows] = useState<PendingRow[]>([])
  const [closedRows, setClosedRows] = useState<ClosedRow[]>([])
  const [loading, setLoading] = useState(false)
  const [action, setAction] = useState<ActionState>(null)
  /** Second-step confirm after FormModal primary action. */
  const [confirming, setConfirming] = useState(false)

  const [closePrice, setClosePrice] = useState('')
  const [partialQty, setPartialQty] = useState('')
  const [editPrice, setEditPrice] = useState('')
  const [editQty, setEditQty] = useState('')

  const clearAction = () => {
    setAction(null)
    setConfirming(false)
  }

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [open, pending, closed] = await Promise.all([
        positionsApi.list({ status: 'open' }),
        positionsApi.list({ status: 'pending' }),
        positionsApi.list({ status: 'closed' }),
      ])
      setOpenRows(open.data.map(mapOpen))
      setPendingRows(pending.data.map(mapPending))
      setClosedRows(closed.data.map(mapClosed))
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : t('blotter.loadFailed')
      pushToast({ tone: 'danger', title: t('blotter.loadFailed'), message })
    } finally {
      setLoading(false)
    }
  }, [pushToast, t])

  useEffect(() => {
    void reload()
    const onChanged = () => void reload()
    window.addEventListener('velox:positions-changed', onChanged)
    return () => window.removeEventListener('velox:positions-changed', onChanged)
  }, [reload])

  const tabs: { id: BlotterTab; label: string; icon: BlotterTab }[] = [
    { id: 'open', label: t('blotter.tabOpen'), icon: 'open' },
    { id: 'pending', label: t('blotter.tabPending'), icon: 'pending' },
    { id: 'closed', label: t('blotter.tabClosed'), icon: 'closed' },
  ]

  const openAction = (next: ActionState) => {
    if (!canTrade && next) {
      pushToast({
        tone: 'danger',
        title: t('common:auth.readOnlyTitle'),
        message: t('common:auth.readOnlyHint'),
      })
      return
    }
    if (next?.kind === 'close') {
      setClosePrice(String(next.row.last))
    } else if (next?.kind === 'partial') {
      setPartialQty(String(next.row.quantity / 2))
      setClosePrice(String(next.row.last))
    } else if (next?.kind === 'edit') {
      setEditPrice(String(next.row.price))
      setEditQty(String(next.row.quantity))
    }
    setAction(next)
  }

  const openColumns: DataTableColumn<PositionRow>[] = useMemo(
    () => [
      {
        id: 'symbol',
        header: t('chart.symbol'),
        sortable: true,
        filterable: true,
        mono: false,
        accessor: (r) => r.symbol,
        filterValue: (r) => r.symbol,
        cell: (r) => (
          <span className="font-medium tracking-tight">{r.symbol}</span>
        ),
      },
      {
        id: 'side',
        header: t('orderTicket.side'),
        sortable: true,
        filterable: true,
        mono: false,
        accessor: (r) => r.side,
        filterValue: (r) => r.side,
        cell: (r) => (
          <span
            className={cn(
              'rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase',
              r.side === 'long' ? 'bg-bid-badge text-bid' : 'bg-ask-badge text-ask',
            )}
          >
            {r.side === 'long' ? t('orderTicket.buy') : t('orderTicket.sell')}
          </span>
        ),
      },
      {
        id: 'qty',
        header: t('orderTicket.quantity'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.quantity,
      },
      {
        id: 'avg',
        header: t('orderTicket.price'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.avgPrice,
        cell: (r) => formatCurrency(r.avgPrice, r.currency),
      },
      {
        id: 'last',
        header: t('watchlist.last'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.last,
        flashKey: (r) => r.last,
        cell: (r) => formatCurrency(r.last, r.currency),
      },
      {
        id: 'pnl',
        header: t('positions.pnl'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.unrealizedPnl,
        flashKey: (r) => r.unrealizedPnl,
        cell: (r) => (
          <span className={r.unrealizedPnl >= 0 ? 'text-bid' : 'text-ask'}>
            {formatCurrency(r.unrealizedPnl, r.currency)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: t('blotter.actions'),
        mono: false,
        align: 'end',
        cell: (r) => (
          <div className="flex justify-end gap-0.5">
            <ActionBtn
              icon="close"
              label={t('blotter.close')}
              onClick={() => openAction({ kind: 'close', row: r })}
            />
            <ActionBtn
              icon="partial"
              label={t('blotter.partialClose')}
              onClick={() => openAction({ kind: 'partial', row: r })}
            />
          </div>
        ),
      },
    ],
    [t],
  )

  const pendingColumns: DataTableColumn<PendingRow>[] = useMemo(
    () => [
      {
        id: 'symbol',
        header: t('chart.symbol'),
        sortable: true,
        filterable: true,
        mono: false,
        accessor: (r) => r.symbol,
        filterValue: (r) => r.symbol,
        cell: (r) => (
          <span className="font-medium tracking-tight">{r.symbol}</span>
        ),
      },
      {
        id: 'side',
        header: t('orderTicket.side'),
        sortable: true,
        filterable: true,
        mono: false,
        accessor: (r) => r.side,
        filterValue: (r) => r.side,
        cell: (r) => (
          <span
            className={cn(
              'rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase',
              r.side === 'buy' ? 'bg-bid-badge text-bid' : 'bg-ask-badge text-ask',
            )}
          >
            {r.side === 'buy' ? t('orderTicket.buy') : t('orderTicket.sell')}
          </span>
        ),
      },
      {
        id: 'type',
        header: t('orderTicket.type'),
        sortable: true,
        accessor: (r) => r.type,
        cell: (r) => (
          <span className="uppercase text-terminal-muted">
            {r.type === 'limit' ? t('orderTicket.limit') : t('blotter.stop')}
          </span>
        ),
      },
      {
        id: 'qty',
        header: t('orderTicket.quantity'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.quantity,
      },
      {
        id: 'price',
        header: t('orderTicket.price'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.price,
        cell: (r) => formatCurrency(r.price, r.currency),
      },
      {
        id: 'actions',
        header: t('blotter.actions'),
        mono: false,
        align: 'end',
        cell: (r) => (
          <div className="flex justify-end gap-0.5">
            <ActionBtn
              icon="edit"
              label={t('blotter.edit')}
              onClick={() => openAction({ kind: 'edit', row: r })}
            />
            <ActionBtn
              icon="delete"
              tone="danger"
              label={t('blotter.delete')}
              onClick={() => openAction({ kind: 'delete', row: r })}
            />
          </div>
        ),
      },
    ],
    [t],
  )

  const closedColumns: DataTableColumn<ClosedRow>[] = useMemo(
    () => [
      {
        id: 'symbol',
        header: t('chart.symbol'),
        sortable: true,
        filterable: true,
        mono: false,
        accessor: (r) => r.symbol,
        filterValue: (r) => r.symbol,
        cell: (r) => (
          <span className="font-medium tracking-tight">{r.symbol}</span>
        ),
      },
      {
        id: 'side',
        header: t('orderTicket.side'),
        sortable: true,
        mono: false,
        accessor: (r) => r.side,
        cell: (r) => (
          <span
            className={cn(
              'rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase',
              r.side === 'long' ? 'bg-bid-badge text-bid' : 'bg-ask-badge text-ask',
            )}
          >
            {r.side === 'long' ? t('orderTicket.buy') : t('orderTicket.sell')}
          </span>
        ),
      },
      {
        id: 'qty',
        header: t('orderTicket.quantity'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.quantity,
      },
      {
        id: 'entry',
        header: t('blotter.entry'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.entryPrice,
        cell: (r) => formatCurrency(r.entryPrice, r.currency),
      },
      {
        id: 'exit',
        header: t('blotter.exit'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.exitPrice,
        cell: (r) => formatCurrency(r.exitPrice, r.currency),
      },
      {
        id: 'pnl',
        header: t('blotter.realizedPnl'),
        align: 'end',
        sortable: true,
        accessor: (r) => r.realizedPnl,
        cell: (r) => (
          <span className={r.realizedPnl >= 0 ? 'text-bid' : 'text-ask'}>
            {formatCurrency(r.realizedPnl, r.currency)}
          </span>
        ),
      },
      {
        id: 'closedAt',
        header: t('blotter.closedAt'),
        sortable: true,
        accessor: (r) => r.closedAt,
      },
    ],
    [t],
  )

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <div className="flex shrink-0 items-center gap-0.5 border-b border-terminal-border/40 px-2 pt-1.5">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              'relative inline-flex h-8 items-center gap-1.5 px-2.5 text-[11px] font-semibold transition-colors',
              tab === item.id
                ? 'text-accent'
                : 'text-terminal-muted hover:text-terminal-fg',
            )}
          >
            <TabIcon name={item.icon} />
            <span className="hidden sm:inline">{item.label}</span>
            <span className="sm:hidden">{item.label.split(' ')[0]}</span>
            {tab === item.id ? (
              <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-accent" />
            ) : null}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1">
        {tab === 'open' ? (
          <DataTable
            className="h-full border-0 rounded-none"
            columns={openColumns}
            rows={openRows}
            rowKey={(r) => r.id}
            pageSize={10}
            pageSizeOptions={[5, 10, 25]}
            exportFilename="open-positions.csv"
            emptyMessage={t('positions.empty')}
          />
        ) : null}
        {tab === 'pending' ? (
          <DataTable
            className="h-full border-0 rounded-none"
            columns={pendingColumns}
            rows={pendingRows}
            rowKey={(r) => r.id}
            pageSize={10}
            pageSizeOptions={[5, 10, 25]}
            exportFilename="pending-orders.csv"
            emptyMessage={t('blotter.emptyPending')}
          />
        ) : null}
        {tab === 'closed' ? (
          <DataTable
            className="h-full border-0 rounded-none"
            columns={closedColumns}
            rows={closedRows}
            rowKey={(r) => r.id}
            pageSize={10}
            pageSizeOptions={[5, 10, 25]}
            exportFilename="closed-positions.csv"
            emptyMessage={
              loading ? t('common:actions.working') : t('blotter.emptyClosed')
            }
          />
        ) : null}
      </div>

      {/* Close position */}
      <FormModal
        open={action?.kind === 'close' && !confirming}
        title={t('blotter.closeTitle')}
        description={
          action?.kind === 'close'
            ? t('blotter.closeDesc', { symbol: action.row.symbol })
            : undefined
        }
        confirmLabel={t('blotter.close')}
        onCancel={clearAction}
        onConfirm={() => setConfirming(true)}
      >
        <ModalField label={t('orderTicket.price')}>
          <input
            className={modalInputClass}
            value={closePrice}
            onChange={(e) => setClosePrice(e.target.value)}
            inputMode="decimal"
          />
        </ModalField>
        <ModalField label={t('orderTicket.quantity')}>
          <input
            className={modalInputClass}
            value={action?.kind === 'close' ? String(action.row.quantity) : ''}
            readOnly
          />
        </ModalField>
      </FormModal>

      {/* Partial close */}
      <FormModal
        open={action?.kind === 'partial' && !confirming}
        title={t('blotter.partialTitle')}
        description={
          action?.kind === 'partial'
            ? t('blotter.partialDesc', { symbol: action.row.symbol })
            : undefined
        }
        confirmLabel={t('blotter.partialClose')}
        disabled={
          action?.kind === 'partial' &&
          (!(Number(partialQty) > 0) ||
            Number(partialQty) >= action.row.quantity)
        }
        onCancel={clearAction}
        onConfirm={() => setConfirming(true)}
      >
        <ModalField label={t('blotter.partialQty')}>
          <input
            className={modalInputClass}
            value={partialQty}
            onChange={(e) => setPartialQty(e.target.value)}
            inputMode="decimal"
          />
        </ModalField>
        <ModalField label={t('orderTicket.price')}>
          <input
            className={modalInputClass}
            value={closePrice}
            onChange={(e) => setClosePrice(e.target.value)}
            inputMode="decimal"
          />
        </ModalField>
        {action?.kind === 'partial' ? (
          <p className="text-[11px] text-terminal-muted">
            {t('blotter.partialMax', { max: action.row.quantity })}
          </p>
        ) : null}
      </FormModal>

      {/* Edit pending */}
      <FormModal
        open={action?.kind === 'edit' && !confirming}
        title={t('blotter.editTitle')}
        description={
          action?.kind === 'edit'
            ? t('blotter.editDesc', { symbol: action.row.symbol })
            : undefined
        }
        confirmLabel={t('blotter.edit')}
        disabled={!(Number(editPrice) > 0) || !(Number(editQty) > 0)}
        onCancel={clearAction}
        onConfirm={() => setConfirming(true)}
      >
        <ModalField label={t('orderTicket.price')}>
          <input
            className={modalInputClass}
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            inputMode="decimal"
          />
        </ModalField>
        <ModalField label={t('orderTicket.quantity')}>
          <input
            className={modalInputClass}
            value={editQty}
            onChange={(e) => setEditQty(e.target.value)}
            inputMode="decimal"
          />
        </ModalField>
      </FormModal>

      {/* Second-step confirm for form actions */}
      <ConfirmModal
        open={
          confirming &&
          (action?.kind === 'close' ||
            action?.kind === 'partial' ||
            action?.kind === 'edit')
        }
        title={t('blotter.confirmProceedTitle')}
        message={t('blotter.confirmProceedMessage')}
        tone={action?.kind === 'edit' ? 'default' : 'danger'}
        confirmLabel={t('common:actions.confirm')}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          void (async () => {
            if (!action) return
            const id = Number(action.row.id)
            try {
              if (action.kind === 'close') {
                await positionsApi.close(id, {
                  all: true,
                  close_price: Number(closePrice) || undefined,
                })
                pushToast({ tone: 'success', title: t('blotter.closeSuccess') })
              } else if (action.kind === 'partial') {
                await positionsApi.close(id, {
                  amount: Number(partialQty),
                  close_price: Number(closePrice) || undefined,
                })
                pushToast({
                  tone: 'success',
                  title: t('blotter.partialSuccess'),
                })
              } else if (action.kind === 'edit') {
                await positionsApi.updatePending(id, {
                  open_price: Number(editPrice),
                  amount: Number(editQty),
                })
                pushToast({ tone: 'success', title: t('blotter.editSuccess') })
              }
              clearAction()
              await reload()
            } catch (err) {
              pushToast({
                tone: 'danger',
                title: t('blotter.actionFailed'),
                message: err instanceof ApiError ? err.message : undefined,
              })
            }
          })()
        }}
      />

      {/* Delete pending — confirm */}
      <ConfirmModal
        open={action?.kind === 'delete'}
        title={t('blotter.deleteTitle')}
        message={
          action?.kind === 'delete'
            ? t('blotter.deleteMessage', { symbol: action.row.symbol })
            : ''
        }
        tone="danger"
        confirmLabel={t('blotter.delete')}
        onCancel={clearAction}
        onConfirm={() => {
          void (async () => {
            if (action?.kind !== 'delete') return
            try {
              await positionsApi.destroy(Number(action.row.id))
              clearAction()
              pushToast({ tone: 'success', title: t('blotter.deleteSuccess') })
              await reload()
            } catch (err) {
              pushToast({
                tone: 'danger',
                title: t('blotter.actionFailed'),
                message: err instanceof ApiError ? err.message : undefined,
              })
            }
          })()
        }}
      />
    </div>
  )
}
